import { inject, Injectable } from '@angular/core';
import { ApiClientService } from './api-client.service';
import { Observable } from 'rxjs';
import { GetPhonesResponse } from '../Models/Responses/GetPhonesResponse';
import { GetAddressesResponse } from '../Models/Responses/GetAddressesResponse';
import { GetEmailsResponse } from '../Models/Responses/GetEmailsResponse';
import { PostNewAddressRequest } from '../Models/Requests/PostNewAddressRequest';
import { GetCustomerDataResponse } from '../Models/Responses/GetCustomerDataResponse';
import { PostInvalidateResponse } from '../Models/Responses/PostInvalidateResponse';
import { PostAddContactDetailsResponse } from '../Models/Responses/PostAddContactDetailsResponse';
import { GetPeselResponse } from '../Models/Responses/GetPeselResponse';
import { GetCalculateScheduleMessage } from '../Models/Responses/GetCalculateScheduleMessage';
import { BaseResponse } from '../Models/Responses/BaseResponse';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly apiService = inject(ApiClientService);

  public getPhoneNumbers(): Observable<GetPhonesResponse> {
    return this.apiService.get<GetPhonesResponse>('api/customers/phones', false);
  }

  public getUserAddresses(): Observable<GetAddressesResponse> {
    return this.apiService.get<GetAddressesResponse>('api/customers/addresses', false);
  }

  public getUserEmails(): Observable<GetEmailsResponse> {
    return this.apiService.get<GetEmailsResponse>('api/customers/emails', false);
  }

  public getUserBaseDetails(): Observable<GetCustomerDataResponse> {
    return this.apiService.get<GetCustomerDataResponse>('api/customers/', false);
  }

  public getUserPesel(): Observable<GetPeselResponse> {
    return this.apiService.get<GetPeselResponse>('api/v2/customers/pesel', false);
  }
  public getCalculateScheduleMessage(): Observable<GetCalculateScheduleMessage> {
    return this.apiService.get<GetCalculateScheduleMessage>('api/v2/customers/calculate-schedule-message', false);
  }

  public invalidateAddress(addressIdHash: string): Observable<PostInvalidateResponse> {
    const params = {
      addressIdHash: addressIdHash,
    };

    return this.apiService.post<PostInvalidateResponse>('api/v2/customers/addresses/invalidate/', false, params);
  }

  public invalidateEmail(emailIdHash: string): Observable<PostInvalidateResponse> {
    const params = {
      emailIdHash: emailIdHash,
    };

    return this.apiService.post<PostInvalidateResponse>('api/v2/customers/emails/invalidate/', false, params);
  }

  public invalidatePhone(phoneIdHash: string): Observable<PostInvalidateResponse> {
    const params = {
      phoneIdHash: phoneIdHash,
    };

    return this.apiService.post<PostInvalidateResponse>('api/v2/customers/phones/invalidate/', false, params);
  }

  public addNewAddress(address: PostNewAddressRequest): Observable<PostAddContactDetailsResponse> {
    const params = {
      addressDto: {
        addressTypeIdHash: address.addressType,
        street: address.street,
        houseNumber: address.houseNumber,
        postCode: address.postCode,
        town: address.town,
        apartmentNumber: address.apartmentNumber ? address.apartmentNumber : '',
      },
    };

    return this.apiService.post<PostAddContactDetailsResponse>('api/v2/customers/addresses/', false, params);
  }

  public addNewPhoneNumber(phoneNumber: string): Observable<PostAddContactDetailsResponse> {
    const phoneDto = {
      phoneDto: {
        phone: phoneNumber,
      },
    };

    return this.apiService.post<PostAddContactDetailsResponse>('api/v2/customers/phones/', false, phoneDto);
  }

  public addNewEmailAddress(emailAddress: string): Observable<PostAddContactDetailsResponse> {
    const params = {
      emailDto: {
        email: emailAddress,
      },
    };

    return this.apiService.post<PostAddContactDetailsResponse>('api/v2/customers/emails/', false, params);
  }

  public autoBind(): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>('api/v2/users/auto-bind', false, null);
  }
}
